import React, { useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  PortalAuthProvider,
  usePortalAuth,
  formatPortalError,
} from "../../lib/portal-auth-context";
import { LOGO_URL } from "../../lib/i18n";
import { ArrowRight, UserPlus, CheckCircle } from "@phosphor-icons/react";

const Form = () => {
  const { user, register } = usePortalAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    phone: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (user === undefined) return null;
  if (user) return <Navigate to="/portal" replace />;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await register({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        password: form.password,
      });
      setDone(true);
      toast.success("Solicitud enviada");
    } catch (err) {
      setError(formatPortalError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[var(--icor-navy)] text-white flex flex-col items-center justify-center px-6 py-16 relative"
      data-testid="portal-register-page"
    >
      <div className="absolute inset-0 bg-grid opacity-25" aria-hidden />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(55% 60% at 50% 0%, rgba(0,82,255,0.22), transparent 60%)",
        }}
        aria-hidden
      />

      <a href="/" className="relative mb-8">
        <img
          src={LOGO_URL}
          alt="ICOR"
          className="h-20 w-auto"
          style={{
            mixBlendMode: "screen",
            filter: "brightness(2.6) contrast(1.1)",
          }}
        />
      </a>

      <div
        className="relative w-full max-w-md bg-white text-slate-900 p-8 md:p-10 border border-white/10"
        style={{ borderRadius: 4 }}
      >
        {done ? (
          <div className="text-center" data-testid="portal-register-success">
            <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 mx-auto flex items-center justify-center">
              <CheckCircle size={30} weight="fill" className="text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight mt-5 text-[var(--icor-navy)]">
              Solicitud enviada
            </h1>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              Un administrador de ICOR revisará tu cuenta y la vinculará con tu
              empresa. Recibirás la confirmación por correo y podrás iniciar
              sesión cuando tu acceso sea aprobado.
            </p>
            <button
              onClick={() => nav("/portal/login")}
              className="btn-primary w-full mt-7"
              data-testid="portal-register-back-to-login"
            >
              Ir al inicio de sesión
              <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)]">
              <UserPlus size={14} weight="bold" />
              Crear cuenta
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
              Portal Cliente ICOR
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Regístrate para acceder a tu historial, facturas y catálogo.
            </p>

            <form
              onSubmit={submit}
              className="mt-7"
              data-testid="portal-register-form"
            >
              {[
                { k: "full_name", label: "Nombre completo", placeholder: "Dr. Juan Pérez", type: "text" },
                { k: "email", label: "Correo corporativo", placeholder: "tu@institucion.com", type: "email" },
                { k: "company", label: "Institución / Empresa", placeholder: "Hospital Ejemplo", type: "text" },
                { k: "phone", label: "Teléfono", placeholder: "+52 55 0000 0000", type: "text" },
                { k: "password", label: "Contraseña (mín. 6 caracteres)", placeholder: "••••••••", type: "password" },
              ].map((f) => (
                <div key={f.k} className="mb-4">
                  <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
                    {f.label}
                  </label>
                  <input
                    type={f.type}
                    required
                    minLength={f.k === "password" ? 6 : undefined}
                    data-testid={`portal-register-${f.k}`}
                    className="icor-input"
                    value={form[f.k]}
                    onChange={(e) => update(f.k, e.target.value)}
                    placeholder={f.placeholder}
                  />
                </div>
              ))}

              {error && (
                <div
                  data-testid="portal-register-error"
                  className="mb-5 p-3 text-sm text-red-700 bg-red-50 border border-red-200"
                  style={{ borderRadius: 4 }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="portal-register-submit"
                className="btn-primary w-full disabled:opacity-60"
              >
                {submitting ? "Enviando..." : "Crear cuenta"}
                <ArrowRight size={16} />
              </button>

              <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600 text-center">
                ¿Ya tienes cuenta?{" "}
                <Link
                  to="/portal/login"
                  className="text-[var(--icor-blue)] font-medium hover:underline"
                >
                  Inicia sesión
                </Link>
              </div>
            </form>
          </>
        )}
      </div>

      <a
        href="/"
        className="relative mt-6 text-xs tracking-[0.18em] uppercase text-white/50 hover:text-white"
      >
        ← Volver al sitio público
      </a>
    </div>
  );
};

export default function PortalRegister() {
  return (
    <PortalAuthProvider>
      <Form />
      <Toaster position="top-right" richColors />
    </PortalAuthProvider>
  );
}
